/**
 * Expected vs Confirmed VAT Adjustments (Підпорція 3.1b, 2026-05-24).
 *
 * Ручне коригування ПДВ-кредиту/зобов'язань. 4 типи коригування:
 *  - REGISTERED_WITH_DIFFERENCE — ПН зареєстрована з помилкою.
 *  - NOT_REGISTERED — ПН не зареєстрована → reverse кредиту.
 *  - CUSTOMS_RECALC — митниця перерахувала.
 *  - OTHER — інше (audit-only).
 *
 * 2026-08-22 (Норма #29): серверна пагінація/сортування через
 * InfiniteDocGrid + колонка «Організація».
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ColDef } from 'ag-grid-community';
import { evcva, type EVCVA, type AdjustmentType } from '@/api/vatAdjustments';
import { orgs } from '@/api/catalogs';
import { useOrgFilter } from '@/store/defaults';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';

type TypeFilter = 'ALL' | AdjustmentType;

const TYPE_LABEL: Record<AdjustmentType, string> = {
  REGISTERED_WITH_DIFFERENCE: 'Зареєстрована з різницею',
  NOT_REGISTERED: 'Не зареєстрована',
  CUSTOMS_RECALC: 'Митниця перерахувала',
  OTHER: 'Інше',
};

const fmt2 = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function EVCVAPage() {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('ALL');
  // 2026-08-11 (рішення власника): орг-фільтр слідує за шапкою.
  const [orgId, setOrgId] = useOrgFilter();
  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const orgName = useMemo(() => {
    const m: Record<string, string> = {};
    (orgsQ.data?.items ?? []).forEach((o) => { m[o.id] = o.name; });
    return m;
  }, [orgsQ.data]);

  const { datasource, total } = useInfiniteGridDatasource<EVCVA>(
    (limit, offset, sort) => evcva.list({
      organization_id: orgId || undefined,
      ...(typeFilter !== 'ALL' ? { adjustment_type: typeFilter } : {}),
      sort_by: sort?.sort_by,
      sort_dir: sort?.sort_dir,
      limit, offset,
    }),
    [orgId, typeFilter],
  );

  const cols = useMemo<ColDef<EVCVA>[]>(
    () => [
      {
        field: 'document_number', headerName: '№', width: 150,
        cellRenderer: (p: { data?: EVCVA }) => p.data ? (
          <span>
            <ImportedBadge source={p.data.external_source} />
            {p.data.external_source ? ' ' : ''}{p.data.document_number}
          </span>
        ) : '',
      },
      {
        field: 'document_date', headerName: 'Дата', width: 170,
        valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString('uk-UA') : '',
      },
      {
        field: 'organization_id', headerName: 'Організація', width: 200,
        valueFormatter: (p) => p.value ? (orgName[p.value] ?? '') : '',
      },
      {
        field: 'adjustment_type', headerName: 'Тип', width: 200,
        valueFormatter: (p) => p.value
          ? (TYPE_LABEL[p.value as AdjustmentType] ?? p.value) : '',
      },
      { field: 'adjustment_reason', headerName: 'Причина', flex: 1, minWidth: 200 },
      {
        field: 'expected_amount', headerName: 'Очікувано', width: 140,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
      },
      {
        field: 'confirmed_amount', headerName: 'Підтверджено', width: 140,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
      },
      {
        field: 'difference', headerName: 'Різниця', width: 140,
        cellStyle: { textAlign: 'right' },
        valueFormatter: (p) => p.value != null ? fmt2(p.value) : '',
      },
      {
        field: 'posted', headerName: 'Проведено', width: 120,
        cellStyle: { textAlign: 'center' },
        valueFormatter: (p) => p.data ? (p.value ? '✓ так' : 'ні') : '',
      },
    ],
    [orgName],
  );

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>Коригування ПДВ<TitleTotal total={total} /></h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}
            style={{ maxWidth: 220 }}>
            <option value="">Усі організації</option>
            {(orgsQ.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <label style={{ fontSize: '0.875rem' }}>Тип:</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TypeFilter)}
            data-testid="evcva-type-filter"
            style={{ width: 140, flex: '0 0 auto' }}
          >
            <option value="ALL">Усі</option>
            <option value="REGISTERED_WITH_DIFFERENCE">Зареєстровані з різницею</option>
            <option value="NOT_REGISTERED">Не зареєстровані</option>
            <option value="CUSTOMS_RECALC">Митниця перерахувала</option>
            <option value="OTHER">Інше</option>
          </select>
        </div>
        <button
          className="primary"
          onClick={() => navigate('/taxation/expected-confirmed-vat-adj/new')}
          style={{ marginLeft: 'auto' }}
        >
          + Нове
        </button>
      </div>
      <InfiniteDocGrid<EVCVA>
        deleteRow={{ remove: (id) => evcva.remove(id) }}
        datasource={datasource}
        columnDefs={cols}
        storageKey="evcva"
        onRowDoubleClicked={(r) => navigate(`/taxation/expected-confirmed-vat-adj/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : undefined}
      />
    </div>
  );
}
